/**
 * Edit Plan Tool - Prompt & Guidelines
 * 
 * Tool description and usage guidelines for modifying presentation plans.
 */

// ═══════════════════════════════════════════════════════════════════════════
// TOOL DESCRIPTION
// ═══════════════════════════════════════════════════════════════════════════

export const EDIT_PLAN_TOOL_DESCRIPTION = `Modify the existing presentation plan. Use this to add, remove, update, move, or reorder sections and slides.

Operation Types:
- add_section: Add a new section (optionally after a specific section)
- remove_section: Remove a section and all its slides
- update_section: Update section name/purpose
- move_section: Move a section to a different position
- add_slide: Add a new slide to a section (optionally after a specific slide)
- remove_slide: Remove a slide from a section
- update_slide: Update slide name/purpose
- move_slide: Move a slide to a different section
- reorder_sections: Reorder all sections by providing new order
- reorder_slides: Reorder slides within a section

Guidelines:
- Operations are applied sequentially
- Maintain plan coherence when editing
- Preserve existing IDs (never change them)
- Explain changes to user in natural language
- If instruction is ambiguous, ask for clarification instead of guessing`;

// ═══════════════════════════════════════════════════════════════════════════
// OPERATION EXAMPLES
// ═══════════════════════════════════════════════════════════════════════════

export const OPERATION_EXAMPLES = `
**Add Section Example:**
\`\`\`json
{
  "type": "add_section",
  "section": {
    "title": "Market Analysis",
    "purpose": "Deep dive into market trends and competitive landscape",
    "slides": [
      { "title": "Market Size", "purpose": "TAM/SAM/SOM breakdown" },
      { "title": "Competitors", "purpose": "Competitive positioning matrix" }
    ]
  },
  "afterSectionId": "section_abc123"
}
\`\`\`

**Add Slide Example:**
\`\`\`json
{
  "type": "add_slide",
  "sectionId": "section_abc123",
  "slide": {
    "title": "Customer Testimonials",
    "purpose": "Social proof with key customer quotes"
  },
  "afterSlideId": "slide_xyz789"
}
\`\`\`

**Update Slide Example:**
\`\`\`json
{
  "type": "update_slide",
  "sectionId": "section_abc123",
  "slideId": "slide_xyz789",
  "updates": {
    "title": "Updated Title",
    "purpose": "New, more specific purpose description"
  }
}
\`\`\`

**Move Slide Between Sections Example:**
\`\`\`json
{
  "type": "move_slide",
  "slideId": "slide_xyz789",
  "fromSectionId": "section_abc123",
  "toSectionId": "section_def456",
  "afterSlideId": "slide_target123"
}
\`\`\``;

// ═══════════════════════════════════════════════════════════════════════════
// BEST PRACTICES
// ═══════════════════════════════════════════════════════════════════════════

export const EDIT_BEST_PRACTICES = `
**Best practices for plan editing:**
1. **Batch related changes**: Group multiple operations that serve the same user intent
2. **Preserve narrative flow**: Ensure edits maintain logical progression through sections
3. **Communicate changes**: Always explain what was changed and why after editing
4. **Validate coherence**: Check that the edited plan still makes sense as a whole
5. **Handle edge cases**: If removing the last slide from a section, consider removing the section`;
